import { CREATURES } from '@battle/content';
import { PROTOCOL_VERSION } from '@battle/shared';
import {
  type CreatureState,
  GRID,
  type Input,
  type Locomotion,
  yawFromU16,
  yawToU16,
} from '@battle/sim';
import { DecodeError, Reader, Writer } from './codec.ts';

/**
 * Wire protocol (docs/05 "Protocol"). One binary WebSocket message = one message below, tagged
 * by its first byte. Client → server tags are < 100, server → client tags are ≥ 100.
 */
export const Tag = {
  Join: 1,
  Inputs: 2,
  Ping: 3,
  Hash: 4,
  Welcome: 101,
  Snapshot: 102,
  Pong: 103,
  Error: 104,
} as const;

/** How many recent inputs each input packet repeats (redundancy against loss). */
export const INPUT_REDUNDANCY = 4;
/** Byte offsets of the per-recipient Snapshot fields, so the server can encode once and patch. */
export const SNAPSHOT_LAST_INPUT_OFFSET = 5;
export const SNAPSHOT_LEAD_OFFSET = 9;

/** Server sends a snapshot every N ticks (60 / 2 = 30 Hz). */
export const SNAPSHOT_EVERY = 2;

export type TickedInput = Input & { tick: number };

export type ClientMsg =
  | { t: typeof Tag.Join; protocol: number; creature: string; name: string }
  | { t: typeof Tag.Inputs; ack: number; inputs: TickedInput[] }
  | { t: typeof Tag.Ping; clientTime: number }
  | { t: typeof Tag.Hash; tick: number; hash: number };

export interface Welcome {
  t: typeof Tag.Welcome;
  protocol: number;
  handle: number;
  serverTick: number;
  room: string;
  gs: string;
  version: string;
}

export interface Snapshot {
  t: typeof Tag.Snapshot;
  serverTick: number;
  /** Latest input tick the server has applied for the recipient (reconciliation base). */
  lastInput: number;
  /** How many ticks ahead of the server the recipient's inputs arrive (<0 = late). */
  inputLead: number;
  /** Server sim time for this tick, ms, for the net graph. */
  tickMs: number;
  creatures: CreatureState[];
}

export type ServerMsg =
  | Welcome
  | Snapshot
  | { t: typeof Tag.Pong; clientTime: number; serverTick: number }
  | { t: typeof Tag.Error; code: number; message: string };

export const ErrorCode = { Protocol: 1, RoomFull: 2, Draining: 3, BadMessage: 4 } as const;

const MODES: Locomotion[] = ['ground', 'air', 'dodge', 'brace'];
const Flag = { grounded: 1, sprinting: 2, sprintLocked: 4, invulnerable: 8 } as const;

const clampInt = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, Math.round(v)));

function writeInput(w: Writer, i: TickedInput) {
  w.u32(i.tick).u16(i.buttons).i8(i.moveX).i8(i.moveY).u16(i.yaw).i16(i.pitch);
}

function readInput(r: Reader): TickedInput {
  return {
    tick: r.u32(),
    buttons: r.u16(),
    moveX: r.i8(),
    moveY: r.i8(),
    yaw: r.u16(),
    pitch: r.i16(),
  };
}

/** Creature state on the sim's network grid (packages/sim grid.ts), so encoding is lossless. */
function writeCreature(w: Writer, s: CreatureState) {
  const kind = CREATURES.findIndex((c) => c.id === s.creatureId);
  w.u8(s.handle).u8(Math.max(0, kind));
  w.i32(Math.round(s.pos.x * GRID.pos))
    .i32(Math.round(s.pos.y * GRID.pos))
    .i32(Math.round(s.pos.z * GRID.pos));
  w.i32(Math.round(s.vel.x * GRID.vel))
    .i32(Math.round(s.vel.y * GRID.vel))
    .i32(Math.round(s.vel.z * GRID.vel));
  w.u16(yawToU16(s.yaw));
  w.u8(MODES.indexOf(s.mode));
  w.u8(
    (s.grounded ? Flag.grounded : 0) |
      (s.sprinting ? Flag.sprinting : 0) |
      (s.sprintLocked ? Flag.sprintLocked : 0) |
      (s.invulnerable ? Flag.invulnerable : 0),
  );
  w.u16(clampInt(s.stamina * GRID.stamina, 0, 65535));
  w.u8(Math.min(255, s.ticksSinceGrounded)).u16(Math.min(65535, s.ticksSinceStaminaUse));
  w.u8(Math.min(255, s.dodgeTick))
    .i16(clampInt(s.dodgeDirX * GRID.dir, -32767, 32767))
    .i16(clampInt(s.dodgeDirZ * GRID.dir, -32767, 32767));
  w.u8(s.bufferedJump).u8(s.bufferedDodge).u16(s.prevButtons);
}

function readCreature(r: Reader): CreatureState {
  const handle = r.u8();
  const kind = r.u8();
  const pos = { x: r.i32() / GRID.pos, y: r.i32() / GRID.pos, z: r.i32() / GRID.pos };
  const vel = { x: r.i32() / GRID.vel, y: r.i32() / GRID.vel, z: r.i32() / GRID.vel };
  const yaw = yawFromU16(r.u16());
  const mode = MODES[r.u8()] ?? 'ground';
  const flags = r.u8();
  const stamina = r.u16() / GRID.stamina;
  const ticksSinceGrounded = r.u8();
  const ticksSinceStaminaUse = r.u16();
  const dodgeTick = r.u8();
  const dodgeDirX = r.i16() / GRID.dir;
  const dodgeDirZ = r.i16() / GRID.dir;
  return {
    handle,
    creatureId: CREATURES[kind]?.id ?? 'kilnback',
    pos,
    vel,
    yaw,
    mode,
    grounded: (flags & Flag.grounded) !== 0,
    sprinting: (flags & Flag.sprinting) !== 0,
    sprintLocked: (flags & Flag.sprintLocked) !== 0,
    invulnerable: (flags & Flag.invulnerable) !== 0,
    stamina,
    ticksSinceGrounded,
    ticksSinceStaminaUse,
    dodgeTick,
    dodgeDirX,
    dodgeDirZ,
    bufferedJump: r.u8(),
    bufferedDodge: r.u8(),
    prevButtons: r.u16(),
  };
}

export function encodeClient(m: ClientMsg): Uint8Array<ArrayBuffer> {
  const w = new Writer(64).u8(m.t);
  switch (m.t) {
    case Tag.Join:
      w.u16(m.protocol).str(m.creature).str(m.name);
      break;
    case Tag.Inputs:
      w.u32(m.ack).u8(m.inputs.length);
      for (const i of m.inputs) writeInput(w, i);
      break;
    case Tag.Ping:
      w.f64(m.clientTime);
      break;
    case Tag.Hash:
      w.u32(m.tick).u32(m.hash);
      break;
  }
  return w.bytes();
}

export function decodeClient(bytes: Uint8Array): ClientMsg {
  const r = new Reader(bytes);
  const t = r.u8();
  switch (t) {
    case Tag.Join:
      return { t, protocol: r.u16(), creature: r.str(), name: r.str() };
    case Tag.Inputs: {
      const ack = r.u32();
      const n = r.u8();
      if (n > 16) throw new DecodeError('too many inputs');
      const inputs = Array.from({ length: n }, () => readInput(r));
      return { t, ack, inputs };
    }
    case Tag.Ping:
      return { t, clientTime: r.f64() };
    case Tag.Hash:
      return { t, tick: r.u32(), hash: r.u32() };
    default:
      throw new DecodeError(`unknown client message ${t}`);
  }
}

export function encodeServer(m: ServerMsg): Uint8Array<ArrayBuffer> {
  const w = new Writer(256).u8(m.t);
  switch (m.t) {
    case Tag.Welcome:
      w.u16(m.protocol).u8(m.handle).u32(m.serverTick).str(m.room).str(m.gs).str(m.version);
      break;
    case Tag.Snapshot:
      w.u32(m.serverTick)
        .u32(m.lastInput)
        .i8(clampInt(m.inputLead, -128, 127))
        .u16(clampInt(m.tickMs * 1000, 0, 65535));
      w.u8(m.creatures.length);
      for (const c of m.creatures) writeCreature(w, c);
      break;
    case Tag.Pong:
      w.f64(m.clientTime).u32(m.serverTick);
      break;
    case Tag.Error:
      w.u8(m.code).str(m.message);
      break;
  }
  return w.bytes();
}

export function decodeServer(bytes: Uint8Array): ServerMsg {
  const r = new Reader(bytes);
  const t = r.u8();
  switch (t) {
    case Tag.Welcome:
      return {
        t,
        protocol: r.u16(),
        handle: r.u8(),
        serverTick: r.u32(),
        room: r.str(),
        gs: r.str(),
        version: r.str(),
      };
    case Tag.Snapshot: {
      const serverTick = r.u32();
      const lastInput = r.u32();
      const inputLead = r.i8();
      const tickMs = r.u16() / 1000;
      const n = r.u8();
      const creatures = Array.from({ length: n }, () => readCreature(r));
      return { t, serverTick, lastInput, inputLead, tickMs, creatures };
    }
    case Tag.Pong:
      return { t, clientTime: r.f64(), serverTick: r.u32() };
    case Tag.Error:
      return { t, code: r.u8(), message: r.str() };
    default:
      throw new DecodeError(`unknown server message ${t}`);
  }
}

export { PROTOCOL_VERSION };
